import { profileBookingSearchLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  getProfileActiveTravelPasses,
  getProfileHistoryTravelPasses,
} from '@/features/profile/profileActions';
import {
  activeTravelPassesProfileSelector,
  historyTravelPassesProfileSelector,
} from '@/features/profile/profileSelectors';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { TransText } from '@/i18n/trans/text';
import BookingNoItems from '@/routes/account/layout/BookingNoItems';
import TravelPassSummary from '@/routes/account/travelPasses/TravelPassSummary';
import { useDispatch, useSelector } from '@/store/utils';
import type { ProfileBookingTimeType } from '@/types/profile';
import { useCallback, useEffect, useMemo, type FC } from 'react';
import { toast } from 'react-toastify';

interface TravelPassesListProps {
  timeType: ProfileBookingTimeType;
}

const TravelPassesList: FC<TravelPassesListProps> = ({ timeType }) => {
  const dispatch = useDispatch();

  const isActive = timeType === 'active';

  const loading = useSelector(profileBookingSearchLoadingSelector);
  const bookingSearchItems = useSelector(
    isActive
      ? activeTravelPassesProfileSelector
      : historyTravelPassesProfileSelector
  );

  // Fetch data on mount if not already loaded
  useEffect(() => {
    if (!bookingSearchItems) {
      const fetchTravelPasses = async () => {
        try {
          await dispatch(
            isActive
              ? getProfileActiveTravelPasses()
              : getProfileHistoryTravelPasses()
          ).unwrap();
        } catch (error) {
          toast.error(
            <TransText
              i18nKey={
                isActive
                  ? 'errorFetchingActiveTravelPasses'
                  : 'errorFetchingHistoryTravelPasses'
              }
            />
          );
        }
      };
      fetchTravelPasses();
    }
  }, [dispatch, isActive, bookingSearchItems]);

  const hasMore = useMemo(() => {
    if (!bookingSearchItems) return false;
    return bookingSearchItems.data.length < bookingSearchItems.totalCount;
  }, [bookingSearchItems]);

  const handleLoadMore = useCallback(async () => {
    const offset = bookingSearchItems?.data.length ?? 0;
    try {
      await dispatch(
        isActive
          ? getProfileActiveTravelPasses(offset)
          : getProfileHistoryTravelPasses(offset)
      ).unwrap();
    } catch (error) {
      toast.error(
        <TransText
          i18nKey={
            isActive
              ? 'errorFetchingActiveTravelPasses'
              : 'errorFetchingHistoryTravelPasses'
          }
        />
      );
    }
  }, [dispatch, isActive, bookingSearchItems]);

  const loadMoreRef = useInfiniteScroll({
    hasMore,
    loading,
    onLoadMore: handleLoadMore,
  });

  if (!bookingSearchItems?.data.length) {
    return <BookingNoItems bookingType="travelPasses" timeType={timeType} />;
  }

  return (
    <div className="flex w-full flex-col laptop:mt-2 laptop:max-w-[690px]">
      <div className="flex w-full flex-col gap-4">
        {bookingSearchItems.data.map((item) => (
          <TravelPassSummary
            key={item.id}
            bookingSearchItem={item}
            isActive={isActive}
          />
        ))}
      </div>
      {hasMore && <div ref={loadMoreRef} />}
    </div>
  );
};

export default TravelPassesList;
